import { AccountRunStatus, AccountSide } from '@hit-game/shared/enums';
import { SIDE_NAMES } from '@hit-game/shared/constants';
import { groupIssues, sideTotal } from '@hit-game/shared/utils';
import type { IAccountDataResponse, IGroup } from '@hit-game/shared/types';
import { motion } from 'framer-motion';
import { AlertTriangle, Check } from 'lucide-react';

import { formatMoneyOrUnknown } from '../../utils/format';
import { groupEligibility } from '../../lib/group-eligibility';
import { GAME_BADGES, GAME_LABELS, MEMBER_COLORS } from '../group/group.constants';

interface GroupSummaryCardProps {
  group: IGroup;
  /** Đã dựng sẵn ở LobbyPage — khỏi mỗi thẻ dựng lại map. */
  accountsById: Map<string, IAccountDataResponse>;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (groupId: string) => void;
}

/** 1000 → `1K`, 1.5tr → `1.5M` — cấu hình cược chỉ cần con số gọn. */
function shortAmount(value: number): string {
  if (value >= 1_000_000) return `${String(value / 1_000_000).replace(/\.0$/, '')}M`;
  if (value >= 1_000) return `${String(value / 1_000).replace(/\.0$/, '')}K`;
  return String(value);
}

/** Tổng tiền hai phe của CẢ nhóm: `Phe A:400K · Phe B:400K`. */
function groupConfig(group: IGroup): string {
  const parts = Object.values(AccountSide)
    .map((side) => ({ side, amount: sideTotal(group.sideConfigs, side) }))
    .filter((row) => row.amount > 0)
    .map((row) => `${SIDE_NAMES[row.side]}:${shortAmount(row.amount)}`);

  return parts.length === 0 ? 'Chưa đặt tiền' : parts.join(' · ');
}

/**
 * Thẻ TÓM TẮT một nhóm — chế độ xem gọn để chọn nhiều nhóm cùng lúc.
 *
 * Giữ đúng những gì cần để chọn: tên + game, thành viên (phe) kèm CẤU HÌNH CƯỢC,
 * trạng thái, số dư, và gợi ý Start/Stop được hay bị bỏ qua (kèm lý do). Điều
 * kiện dùng CHUNG luật với thẻ chi tiết + bulk qua `groupEligibility`.
 */
export function GroupSummaryCard({
  group,
  accountsById,
  selectable,
  selected,
  onToggleSelect,
}: GroupSummaryCardProps) {
  const groupId = String(group._id);
  const resolved = group.members.map((member) => accountsById.get(String(member.accountId)));

  const running = resolved.some((account) => account?.status === AccountRunStatus.RUNNING);
  const allLoggedIn =
    group.members.length > 0 && resolved.every((account) => account?.isLoggedIn === true);
  const issues = groupIssues({
    gameType: group.gameType,
    memberCount: group.members.length,
    sideConfigs: group.sideConfigs,
  });

  const eligibility = groupEligibility(group, accountsById);
  const hint = running
    ? { tone: 'stop' as const, text: 'Stop được' }
    : eligibility.start.ok
      ? { tone: 'start' as const, text: 'Start được' }
      : { tone: 'skip' as const, text: `Bỏ qua · ${eligibility.start.reason}` };

  const statusPill = running
    ? { cls: 'bg-success/10 text-success-bright', dot: 'bg-success', text: 'Đang chạy' }
    : allLoggedIn
      ? { cls: 'bg-panel-hover text-ink-dim', dot: 'bg-ink-dim', text: 'Đã đăng nhập' }
      : { cls: 'bg-panel-hover text-ink-muted', dot: 'bg-ink-faint', text: 'Chưa đăng nhập' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`bg-panel relative rounded-xl border p-3.5 shadow-sm transition-shadow ${
        selected ? 'border-accent ring-accent ring-2' : 'border-line'
      }`}
    >
      {selectable && (
        <button
          type="button"
          onClick={() => onToggleSelect(groupId)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Bỏ chọn' : 'Chọn'} nhóm ${group.name}`}
          className="absolute inset-0 z-20 cursor-pointer rounded-xl"
        />
      )}
      {selectable && (
        <span
          className={`pointer-events-none absolute right-2.5 top-2.5 z-30 grid h-5 w-5 place-items-center rounded-md border-2 transition-colors ${
            selected ? 'border-accent bg-accent text-white' : 'border-line bg-canvas'
          }`}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
      )}

      {/* Tên + game */}
      <div className="mb-2.5 flex items-center gap-2 pr-6">
        <h3 className="text-ink truncate text-sm font-bold">{group.name}</h3>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
            GAME_BADGES[group.gameType] ?? 'bg-panel-hover text-ink-dim'
          }`}
          title={GAME_LABELS[group.gameType] ?? group.gameType}
        >
          {group.gameType}
        </span>
        {issues.length > 0 && (
          <span
            className="bg-danger-bg text-danger inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase"
            title={`Chưa chạy được — ${issues.join('; ')}`}
          >
            <AlertTriangle size={9} />
            Chưa đủ
          </span>
        )}
        <span className="text-ink-dim shrink-0 font-mono text-[10px]">{groupConfig(group)}</span>
      </div>

      {/* Thành viên — KHÔNG còn phe: phe được random lại mỗi ván. */}
      <div className="mb-2.5 space-y-1">
        {group.members.length > 0 ? (
          group.members.map((member, index) => {
            const account = accountsById.get(String(member.accountId));
            return (
              <div key={String(member.accountId)} className="flex items-center gap-2 text-[11.5px]">
                <span
                  className="min-w-0 flex-1 truncate font-medium"
                  style={{ color: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
                >
                  {account?.username ?? '—'}
                </span>
                <span className="text-ink shrink-0 font-mono text-[11px] tabular-nums">
                  {formatMoneyOrUnknown(account?.balance)}
                </span>
              </div>
            );
          })
        ) : (
          <span className="text-ink-muted text-[11.5px]">Chưa có thành viên</span>
        )}
      </div>

      {/* Trạng thái + gợi ý hành động */}
      <div className="border-line-soft flex flex-wrap items-center gap-2 border-t pt-2.5">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusPill.cls}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusPill.dot}`} />
          {statusPill.text}
        </span>
        <span
          className={`ml-auto inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold ${
            hint.tone === 'start'
              ? 'bg-success/10 text-success-bright'
              : hint.tone === 'stop'
                ? 'bg-danger/10 text-danger'
                : 'bg-warning/10 text-warning'
          }`}
        >
          {hint.text}
        </span>
      </div>
    </motion.div>
  );
}
